import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MyFireWorks from '../Fireworks';
import celebrate from '../../../../../../assets/celebrate.png';
import mindset from '../../../../../../assets/selfawareness-images/mindset.png';
import QuestionFromVideo from './QuestionFromVideo';
import WeekThreeAssessmentForm from './WeekThreeAssessmentForm';
import QuestionComponent from './QuestionComponent';
import userService from '../../../../../../services/api/user.js';
import EndOfCourseComponent from './EndOfCourseComponent.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { updateData } from '../../../../../../redux/reducers/userAnswersReducer.js';

import ProgressionButtons from '../components/ProgressionButtons.jsx';
import VideoComponent from '../components/VideoComponent.jsx';
import SystemFeedbackModal from '../../systemFeedback/SystemFeedbackModal.jsx';
import useSelfAwarenessSystemFeedback from '../systemFeedback/useSelfAwarenessSystemFeedback.js';
import {
	getLegacySelfAwarenessActivityDataKey,
	getLegacySelfAwarenessActivityProgressKey,
	getSelfAwarenessActivityDataKey,
	getSelfAwarenessActivityProgressKey,
	mergeSelfAwarenessActivityDrafts,
	readSelfAwarenessStorage,
	writeSelfAwarenessStorage,
} from '../utils/storage';

export default function WeekThreeLearning({
	course,
	courseId,
	onClose,
	currentWeekIndex,
	handleLinkClick,
	requestedActivity,
	onActivityChange,
}) {
	const dispatch = useDispatch();
	const hasRestoredRemoteActivity = useRef(false);
	const [showPopup, setShowPopup] = useState(false);
	const [currentActivity, setCurrentActivity] = useState(() => {
		return readSelfAwarenessStorage(
			getSelfAwarenessActivityProgressKey(courseId, currentWeekIndex),
			getLegacySelfAwarenessActivityProgressKey(currentWeekIndex),
			1
		);
	});

	const [formData, setFormData] = useState(() => {
		return readSelfAwarenessStorage(
			getSelfAwarenessActivityDataKey(courseId, 3),
			getLegacySelfAwarenessActivityDataKey(3),
			{ week: 3, activities: [] }
		);
	});

	const week = 3;
	const { advanceAfterSave, continueAfterFeedback, feedback: systemFeedback } =
		useSelfAwarenessSystemFeedback(week, setCurrentActivity);
	const { data: courseData, isLoading, status, isError } = useQuery({
		queryKey: ['self-awareness-course-3', courseId, week],
		queryFn: () => userService.getUserCourseData(courseId, week),
		refetchOnMount: 'always',
		refetchOnWindowFocus: true,
		keepPreviousData: false,
	});

	useEffect(() => {
		if (!courseData) return;

		if (courseData.activity) {
			const activities = courseData.activity.activities || [];

			// Remote State Restoration: Jump to last saved page if it exists and week is NOT completed
			if (!hasRestoredRemoteActivity.current && courseData.activity.lastActivityIndex && !isCompleted) {
				setCurrentActivity(courseData.activity.lastActivityIndex);
			}
			hasRestoredRemoteActivity.current = true;

			setFormData((prevData) => {
				const draftActivities = prevData?.activities || [];
				return {
					...prevData,
					week,
					activities: mergeSelfAwarenessActivityDrafts(activities, draftActivities),
				};
			});

			dispatch(
				updateData({
					course: course?.course?._id,
					courseEnrollmentId: courseId,
					week,
					activities: activities,
					assessments: courseData.assessment?.assessments || [],
				})
			);
		}

		if (courseData.assessment) {
			const assessments = courseData.assessment.assessments || [];
			const percent = courseData.assessment.rating;

			const assessment_data = {
				week: week,
				percentage: percent,
				assessments: assessments,
			};

			writeSelfAwarenessStorage(
				'weekThreeAssessmentData',
				{ formattedData: assessment_data }
			);
		}
	}, [courseData]);

	const [videoPlaying, setVideoPlaying] = useState(false);
	const [reviewPopUp, setReviewPopUp] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		writeSelfAwarenessStorage(
			getSelfAwarenessActivityProgressKey(courseId, currentWeekIndex),
			currentActivity
		);
		onActivityChange?.(currentWeekIndex, currentActivity);
	}, [courseId, currentActivity, currentWeekIndex, onActivityChange]);

	useEffect(() => {
		if (requestedActivity?.week !== currentWeekIndex) return;
		if (!requestedActivity?.activity) return;
		setCurrentActivity(requestedActivity.activity);
	}, [currentWeekIndex, requestedActivity]);

	useEffect(() => {
		writeSelfAwarenessStorage(
			getSelfAwarenessActivityDataKey(courseId, 3),
			formData
		);
	}, [courseId, formData]);

	const isCompleted = !!courseData?.assessment;

	const queryClient = useQueryClient();

	const onLocalUpdate = useCallback((incomingData) => {
		setFormData((prevData) => {
			const currentActivities = prevData?.activities || courseData?.activity?.activities || [];
			const updatedActivities = currentActivities.map((item) =>
				item.activity === currentActivity ? { ...item, ...incomingData } : item
			);

			if (!updatedActivities.find((item) => item.activity === currentActivity)) {
				updatedActivities.push({ activity: currentActivity, ...incomingData });
			}

			return { ...prevData, activities: updatedActivities };
		});
	}, [currentActivity, courseData?.activity?.activities]);

	const handleNext = useCallback(async (incomingData = {}) => {
		const currentActivities = formData?.activities || courseData?.activity?.activities || [];
		const updatedActivities = currentActivities.map((item) =>
			item.activity === currentActivity ? { ...item, ...incomingData } : item
		);

		if (!updatedActivities.find((item) => item.activity === currentActivity)) {
			updatedActivities.push({ activity: currentActivity, ...incomingData });
		}

		setFormData((prevData) => ({ ...prevData, activities: updatedActivities }));

		const nextActivity = currentActivity >= 8 ? 9 : currentActivity + 1;

		if (!isCompleted && !isLoading) {
			const payload = {
				week: week,
				activities: updatedActivities,
				lastActivityIndex: nextActivity // Save where they are going
			};

			try {
				const result = await userService.postMyActivity(courseId, payload);
				queryClient.setQueryData(
					['self-awareness-course-3', courseId, week],
					(previousData) => ({
						...(previousData || {}),
						activity: result?.newActivity || {
							...(previousData?.activity || {}),
							...payload,
						},
					})
				);
				await queryClient.invalidateQueries({ queryKey: ['enrollment', courseId] });
			} catch (err) {
				console.error("Failed to auto-save activity:", err);
				return false;
			}
		}
		advanceAfterSave(currentActivity, nextActivity);
		return true;
	}, [advanceAfterSave, formData?.activities, courseData?.activity?.activities, currentActivity, courseId, isCompleted, isLoading, queryClient]);

	const handlePrevious = () => {
		setCurrentActivity((prev) => prev - 1);
	};

	const closeReviewPopUp = () => setReviewPopUp(false);

	const handleNextWeekCourse = () => {
		const nextWeekIndex = currentWeekIndex + 1;
		if (handleLinkClick) {
			handleLinkClick(nextWeekIndex - 1, 1);
			return;
		}
		navigate(`/dashboard/self-awareness-course`, {
			state: { enrollmentData: course, weekIndex: nextWeekIndex },
		});
	};

	// console.log(formData)
	const renderActivityContent = () => {
		switch (currentActivity) {
			case 1:
				return (
					<>
						<VideoComponent
							videoPlaying={videoPlaying}
							setVideoPlaying={setVideoPlaying}
							videoSrc="https://d3sc34m1n26ele.cloudfront.net/self-awareness-week-3/FLOW-3_1.mp4"
						/>
						<div className="mt-3">
							<ProgressionButtons variant="next" onClickNext={() => handleNext()} />
						</div>
					</>
				);
			case 2:
				return (
					<QuestionComponent
						activityIndex={currentActivity}
						questionText={'What do you understand by the word'}
						imageSrc={mindset}
						formData={formData}
						altText="?"
						onBack={handlePrevious}
						onUpdate={onLocalUpdate}
						onNext={(answers) =>
							handleNext({
								answers,
							})
						}
					/>
				);
			case 3:
				return (
					<>
						<VideoComponent
							videoPlaying={videoPlaying}
							setVideoPlaying={setVideoPlaying}
							videoSrc="https://d3sc34m1n26ele.cloudfront.net/self-awareness-week-3/FLOW-3_2.mp4"
						/>
						<div className="mt-3">
							<ProgressionButtons
								variant="both"
								onClickNext={() => handleNext()}
								onClickPrev={handlePrevious}
							/>
						</div>
					</>
				);
			case 4:
				return (
					<QuestionComponent
						activityIndex={currentActivity}
						questionText={
							'Do you feel like you have a growth mindset, or do you sometimes find yourself with a fixed mindset? Share your thoughts. It’s okay to be honest, this is all about learning and growing together!'
						}
						formData={formData}
						altText={''}
						onBack={handlePrevious}
						onUpdate={onLocalUpdate}
						onNext={(answers) =>
							handleNext({
								answers,
							})
						}
					/>
				);
			case 5:
				return (
					<>
						<VideoComponent
							videoPlaying={videoPlaying}
							setVideoPlaying={setVideoPlaying}
							videoSrc="https://d3sc34m1n26ele.cloudfront.net/self-awareness-week-3/FLOW-3_3.mp4"
						/>
						<div className="mt-3">
							<ProgressionButtons
								variant="both"
								onClickNext={() => handleNext()}
								onClickPrev={handlePrevious}
							/>
						</div>
					</>
				);
			case 6:
				return (
					<QuestionFromVideo
						activityIndex={currentActivity}
						formData={formData}
						onBack={handlePrevious}
						onNext={(answers) =>
							handleNext({
								answers,
							})
						}
					/>
				);
			case 7:
				return (
					<>
						<VideoComponent
							videoPlaying={videoPlaying}
							setVideoPlaying={setVideoPlaying}
							videoSrc="https://d3sc34m1n26ele.cloudfront.net/self-awareness-week-3/FLOW-3_4.mp4"
						/>
						<div className="mt-3">
							<ProgressionButtons
								variant="both"
								onClickNext={() => handleNext()}
								onClickPrev={handlePrevious}
							/>
						</div>
					</>
				);

			case 8:
				return (
					<WeekThreeAssessmentForm
						onBack={handlePrevious}
						handleNextWeekCourse={handleNextWeekCourse}
						onNext={handleNext}
						course={course}
						activityData={formData}
						savedAssessment={courseData?.assessment}
						isCompleted={isCompleted}
					/>
				);
			default:
				return (
					<EndOfCourseComponent
						onNextWeekCourse={handleNextWeekCourse}
						onClose={onClose}
						handleLinkClick={handleLinkClick}
						setCurrentActivity={setCurrentActivity}
						openReviewPopUp={() => setReviewPopUp(true)}
					/>
				);
		}
	};

	return (
		<div className="week-learning">
			<div className="content-container">
				{renderActivityContent()}
				<SystemFeedbackModal feedback={systemFeedback} onContinue={continueAfterFeedback} />
			</div>
		</div>
	);
}
